import { useRef, useState, useEffect } from 'react';
import { useReducedMotion } from './useReducedMotion';

interface ScrollRevealOptions {
  threshold?: number;
  once?: boolean;
}

interface ScrollRevealResult<T extends HTMLElement> {
  ref: React.RefObject<T | null>;
  isVisible: boolean;
}

export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options: ScrollRevealOptions = {},
): ScrollRevealResult<T> {
  const { threshold = 0.15, once = true } = options;
  const ref = useRef<T | null>(null);
  const reduced = useReducedMotion();
  const [isVisible, setIsVisible] = useState(reduced);

  useEffect(() => {
    if (reduced) {
      setIsVisible(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(el);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, once, reduced]);

  return { ref, isVisible };
}
